import brief1Raw from '@/data/brieven/brief-1-verlaging.txt?raw'
import bericht1Raw from '@/data/brieven/bericht-1-verlaging.txt?raw'
import brief1EnRaw from '@/data/brieven/brief-1-verlaging-en.txt?raw'
import bericht1EnRaw from '@/data/brieven/bericht-1-verlaging-en.txt?raw'
import { useLocale } from './useLocale'

export function useBriefGenerator() {
  const { t, locale } = useLocale()

  function isEn() {
    return locale.value === 'en'
  }

  function getTemplateData(templateKey) {
    const briefTekst = isEn() ? brief1EnRaw : brief1Raw
    const berichtTekst = isEn() ? bericht1EnRaw : bericht1Raw

    const templates = {
      'brief-verlaging': {
        titel: t('brief.title'),
        sub: t('brief.subBlanco.brief'),
        filename: 'brief-verlaging.txt',
        variant: 'brief',
        heeft_email: false,
        email: null,
        kanaal_hint: t('brief.hint.blancoBrief'),
        mailto_subject: null,
        tekst: briefTekst,
        info: [
          { type: 'intro', text: t('brief.info.introBlancoBrief') },
          { type: 'fill', label: t('brief.info.fillYellow'), title: t('brief.info.personalTitle'), text: t('brief.info.personalText') },
          { type: 'computed', label: t('brief.info.fillBlue'), title: t('brief.info.verstrekkerTitle'), text: t('brief.info.verstrekkerText') },
          { type: 'fill', label: t('brief.info.attachment'), title: t('brief.info.wozBeschikkingTitle'), text: t('brief.info.wozBeschikkingText') }
        ]
      },
      'bericht-verlaging': {
        titel: t('brief.title'),
        sub: t('brief.subBlanco.bericht'),
        filename: 'bericht-verlaging.txt',
        variant: 'bericht',
        heeft_email: false,
        email: null,
        kanaal_hint: t('brief.hint.blancoBericht'),
        mailto_subject: null,
        tekst: berichtTekst,
        info: [
          { type: 'intro', text: t('brief.info.introBlancoBericht') },
          { type: 'fill', label: t('brief.info.fillYellow'), title: t('brief.info.hypnrTitle'), text: t('brief.info.hypnrText') },
          { type: 'computed', label: t('brief.info.fillBlue'), title: t('brief.info.wozSchuldTitle'), text: t('brief.info.wozSchuldText') },
          { type: 'fill', label: t('brief.info.keepReady'), title: t('brief.info.wozBeschikkingTitle'), text: t('brief.info.wozBeschikkingBericht') }
        ]
      },
    }

    return templates[templateKey] || null
  }

  function buildBriefData(briefNummer, resultData) {
    if (!resultData) return null

    const formatEuro = resultData.formatEuro
    const verstrekker = resultData.verstrekker
    const heeftEmail = !!verstrekker.email
    const briefSafeNaam = verstrekker.naam.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    const en = isEn()
    const vandaag = new Date().toLocaleDateString(en ? 'en-GB' : 'nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
    const now = new Date()
    const peiljaar = now.getMonth() >= 2 ? now.getFullYear() - 1 : now.getFullYear() - 2
    const peildatum = en ? `1 January ${peiljaar}` : `1 januari ${peiljaar}`

    const kanaalHint = en ? (verstrekker.kanaal_hint_en || verstrekker.kanaal_hint) : verstrekker.kanaal_hint

    if (briefNummer === 1) {
      if (!heeftEmail) {
        const tekst = en
          ? `Hello,\n\nI would like to have the risk surcharge on my mortgage reviewed.\n\nMortgage number: {{p:hypnr=Enter your mortgage number}}\n\nAccording to the most recent WOZ assessment (reference date ${peildatum}), the market value of my property is {{c:woz=${formatEuro(resultData.woz.waarde)}}}. My remaining mortgage debt is {{c:schuld=${formatEuro(resultData.schuld)}}}, which amounts to a loan-to-value ratio of {{c:ltv=${resultData.ltv}%}}.\n\nBased on the current rate table, I should fall into the risk category up to {{c:categorie=${resultData.huidigeBracket.max}%}}. I request that you verify whether my risk surcharge corresponds with this category, and adjust it if necessary.\n\nCould you send me confirmation of the review and any adjustment, including the effective date and the new interest rate?\n\nKind regards,\n{{p:naam=Your name}}`
          : `Goedendag,\n\nIk wil graag de risico-opslag op mijn hypotheek laten controleren.\n\nHypotheeknummer: {{p:hypnr=Vul je hypotheeknummer in}}\n\nVolgens de meest recente WOZ beschikking (peildatum ${peildatum}) is de marktwaarde van mijn woning {{c:woz=${formatEuro(resultData.woz.waarde)}}}. Mijn resterende hypotheekschuld bedraagt {{c:schuld=${formatEuro(resultData.schuld)}}}, wat neerkomt op een schuld markt waardeverhouding van {{c:ltv=${resultData.ltv}%}}.\n\nOp basis van de actuele tarieventabel hoor ik daarmee in de risico categorie tot {{c:categorie=${resultData.huidigeBracket.max}%}}. Ik verzoek u te controleren of mijn risico-opslag overeenkomt met deze categorie, en deze zo nodig aan te passen.\n\nKunt u mij een bevestiging sturen van de controle en eventuele aanpassing, inclusief de ingangsdatum en het nieuwe rentepercentage?\n\nMet vriendelijke groet,\n{{p:naam=Jouw naam}}`

        return {
          titel: t('brief.title'),
          sub: t('brief.subPersonal.bericht', { verstrekker: verstrekker.naam }),
          filename: `bericht-verlaging-${briefSafeNaam}.txt`,
          variant: 'bericht',
          heeft_email: false,
          email: null,
          kanaal_hint: kanaalHint,
          mailto_subject: null,
          tekst,
          info: [
            { type: 'intro', text: t('brief.info.introPersonalBericht', { verstrekker: verstrekker.naam }) },
            { type: 'fill', label: t('brief.info.fill'), title: t('brief.info.hypnrPersonalTitle'), text: t('brief.info.hypnrPersonalText') },
            { type: 'computed', label: t('brief.info.computed'), title: t('brief.info.wozSchuldPersonalTitle'), text: t('brief.info.wozSchuldPersonalText') },
            { type: 'fill', label: t('brief.info.keepReady'), title: t('brief.info.wozBeschikkingTitle'), text: t('brief.info.wozBeschikkingBerichtText', { peiljaar }) }
          ]
        }
      }

      const greeting = en ? 'Dear Sir/Madam,' : 'Geachte heer/mevrouw,'
      const subject = en ? 'Subject: Request to reduce the risk surcharge on my mortgage' : 'Betreft: Verzoek tot verlaging van de risico-opslag op mijn hypotheek'
      const hypLabel = en ? 'Mortgage number:' : 'Hypotheeknummer:'
      const hypPlaceholder = en ? 'Enter your mortgage number' : 'Vul je hypotheeknummer in'
      const nameLabel = en ? 'Your name' : 'Jouw naam'
      const addrLabel = en ? 'Your street and house number' : 'Jouw straat en huisnummer'
      const pcLabel = en ? 'Postcode + city' : 'Postcode + woonplaats'
      const closingGreet = en ? 'Kind regards,' : 'Met vriendelijke groet,'
      const attachLabel = en ? 'Attachment: WOZ assessment' : 'Bijlage: WOZ beschikking'

      const bodyP1 = en
        ? `I hereby request a reduction of the risk surcharge on my mortgage with ${verstrekker.naam}.`
        : `Hierbij verzoek ik u om verlaging van de risico-opslag op mijn hypotheek bij ${verstrekker.naam}.`
      const bodyP2 = en
        ? `According to the most recent WOZ assessment with reference date ${peildatum}, the market value of my property is {{c:woz=${formatEuro(resultData.woz.waarde)}}}. My remaining mortgage debt is {{c:schuld=${formatEuro(resultData.schuld)}}}, which amounts to a loan-to-value ratio of {{c:ltv=${resultData.ltv}%}}.`
        : `Volgens de meest recente WOZ beschikking met peildatum ${peildatum} bedraagt de marktwaarde van mijn woning {{c:woz=${formatEuro(resultData.woz.waarde)}}}. Mijn resterende hypotheekschuld bedraagt {{c:schuld=${formatEuro(resultData.schuld)}}}, wat neerkomt op een schuld markt waardeverhouding van {{c:ltv=${resultData.ltv}%}}.`
      const bodyP3 = en
        ? `Based on your current rate table, I should fall into the risk category up to {{c:categorie=${resultData.huidigeBracket.max}%}}. I request that you verify whether my risk surcharge corresponds with this category, and adjust it if necessary.`
        : `Op basis van uw actuele tarieventabel hoor ik daarmee in de risico categorie tot {{c:categorie=${resultData.huidigeBracket.max}%}}. Ik verzoek u te controleren of mijn risico-opslag overeenkomt met deze categorie, en deze zo nodig aan te passen.`
      const bodyP4 = en
        ? 'I enclose a copy of the WOZ assessment as an attachment.'
        : 'Een kopie van de WOZ beschikking voeg ik bij als bijlage.'
      const bodyP5 = en
        ? 'I would appreciate written confirmation of the review and any adjustment, including the effective date and the new interest rate.'
        : 'Graag ontvang ik een schriftelijke bevestiging van de controle en eventuele aanpassing, inclusief de ingangsdatum en het nieuwe rentepercentage.'

      const tekst = `{{p:naam=${nameLabel}}}\n{{p:adres=${addrLabel}}}\n{{p:postcode=${pcLabel}}}\n\n${verstrekker.naam}\n${verstrekker.adres}\n\n${vandaag}\n\n${subject}\n${hypLabel} {{p:hypnr=${hypPlaceholder}}}\n\n${greeting}\n\n${bodyP1}\n\n${bodyP2}\n\n${bodyP3}\n\n${bodyP4}\n\n${bodyP5}\n\n${closingGreet}\n\n{{p:naam2=${nameLabel}}}\n{{p:tel=${en ? 'Phone number' : 'Telefoonnummer'}}}\n{{p:mail=${en ? 'Email address' : 'E-mailadres'}}}\n\n${attachLabel} ${peiljaar}`

      return {
        titel: t('brief.title'),
        sub: t('brief.subPersonal.brief', { verstrekker: verstrekker.naam }),
        filename: `brief-verlaging-${briefSafeNaam}.txt`,
        variant: 'brief',
        heeft_email: true,
        email: verstrekker.email,
        kanaal_hint: null,
        mailto_subject: t('brief.mailtoSubject'),
        tekst,
        info: [
          { type: 'intro', text: t('brief.info.introPersonalBrief') },
          { type: 'fill', label: t('brief.info.fill'), title: t('brief.info.nameAddressTitle'), text: t('brief.info.nameAddressText') },
          { type: 'fill', label: t('brief.info.fill'), title: t('brief.info.hypnrPersonalTitle'), text: t('brief.info.hypnrPersonalText') },
          { type: 'computed', label: t('brief.info.computed'), title: t('brief.info.wozSchuldPersonalTitle'), text: t('brief.info.wozSchuldPersonalText') },
          { type: 'computed', label: t('brief.info.computed'), title: t('brief.info.ltvCategorieTitle'), text: t('brief.info.ltvCategorieText', { verstrekker: verstrekker.naam }) },
          { type: 'fill', label: t('brief.info.attachment'), title: t('brief.info.wozBeschikkingTitle'), text: t('brief.info.wozBeschikkingPersonalText', { peiljaar }) }
        ]
      }
    }

  }

  return {
    getTemplateData,
    buildBriefData
  }
}
